import {
  type ChangeAcknowledgement,
  acknowledgementOf,
  type ChangeConsequences,
  type GoalOption,
  type GoalOptionSet,
  GOAL_OPTIONS_POLL_MS,
  GOAL_OPTIONS_WAIT_MS,
  canonicalJson,
  chosenGoalOption,
  goalOptionsRequestNeeded,
  sameGoalOptionsInputs,
} from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type GoalView, fetchGoal, requestGoalOptions, selectGoal } from '../api/client.ts';
import { domainCodeOf } from '../api/errors.ts';
import { ChangeConfirm, unacknowledgedChangeOf } from './ChangeConsequences.tsx';
import { GOAL_INPUTS_CHANGED_NOTICE, GOAL_RECOMMENDED_WHY_LABEL } from './goal-copy.ts';
import { VersionHistory } from './VersionHistory.tsx';
import { Pending } from '../ui/Pending.tsx';
import { Tooltip } from '../ui/Tooltip.tsx';

export interface GoalStepProps {
  studyId: string;
  /**
   * Whether the study's inputs are fixed: paid for, frozen or launched. The step is then shown as
   * it stands, asking for nothing and offering no choice the server would refuse.
   */
  frozen: boolean;
  /** Bumped by the shell whenever the study may have changed; the step is read again on it. */
  refreshToken: number;
  /** Tells the shell the Goal changed, so the steps and the sheet are read again. */
  onStudyChanged: () => void;
}

const failureMessage = (failure: unknown, fallback: string): string =>
  failure instanceof Error ? failure.message : fallback;

/** One goal's substance: what it informs, and what it will and will not tell. */
const GoalFacts = ({ option }: { option: GoalOption }) => (
  <dl className="goal-facts">
    <dt>Informs the decision</dt>
    <dd data-test-id="goal-decision">{option.decision}</dd>
    <dt>You will learn</dt>
    <dd data-test-id="goal-learn">{option.learn}</dd>
    <dt>You will not learn</dt>
    <dd data-test-id="goal-not-learn">{option.not_learn}</dd>
  </dl>
);

/**
 * A goal's title, with the Recommended tag and, for the goal Sigora recommends, a `?` whose
 * tooltip says why. The reason shows on hover or focus, and a click or tap pins it open; it is a
 * tooltip rather than a disclosure, so it never pushes the options apart.
 */
const GoalHead = ({ option, titleTestId }: { option: GoalOption; titleTestId: string }) => (
  <div className="goal-option-head">
    <h3 className="goal-title" data-test-id={titleTestId}>
      {option.title}
    </h3>
    {option.recommended && (
      <span className="goal-tag" data-test-id="goal-recommended">
        Recommended
      </span>
    )}
    {option.rationale !== null && (
      <Tooltip content={option.rationale} align="end" testId="goal-rationale" toggleOnClick>
        <button
          type="button"
          className="goal-why"
          aria-label={GOAL_RECOMMENDED_WHY_LABEL}
          data-test-id="goal-why-recommended"
        >
          <span className="goal-why-mark" aria-hidden="true">
            ?
          </span>
        </button>
      </Tooltip>
    )}
  </div>
);

/** A history entry's line: the goal that version chose, then why the version exists. */
const historyLine = (entry: GoalView['history'][number]): string =>
  entry.reason === '' ? entry.chosenTitle : `${entry.chosenTitle}. ${entry.reason}`;

/**
 * The Goal step: three goals Sigora drafted from the Study Type and the confirmed Materials, one of
 * them Recommended, and "Use this goal", which chooses and approves one in a single request.
 *
 * Everything shown is the server's: the options, the request behind them, the chosen Goal and every
 * version of it with the reason it exists. So a refresh restores the step by reading it again, and
 * nothing here decides what the server will accept — a choice names the set, the option and the
 * Goal version on screen, and the server refuses one made over anything that moved.
 *
 * The chosen Goal is marked where it is in the list, in the shared selected style. One chosen from
 * options the study no longer offers is shown above the list instead, still selected. Its version and
 * history sit behind one toggle below the list.
 *
 * Options are asked for on arrival, once, when the study holds none drafted for it as it stands, and
 * the step then polls until the request settles or the wait runs out. "Try again" asks regardless.
 * Reshaping them is done in the step's conversation, whose turns end with the step read again.
 */
export const GoalStep = ({ studyId, frozen, refreshToken, onStudyChanged }: GoalStepProps) => {
  const [view, setView] = useState<GoalView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Why the step was read again under the reader, said once over it. */
  const [notice, setNotice] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  /** The option being chosen while its request is out. */
  const [choosing, setChoosing] = useState<string | null>(null);
  const [chooseError, setChooseError] = useState<string | null>(null);
  /**
   * The option whose change of Goal is waiting on the reader's say-so, with what the server said
   * the change would do below the Goal.
   */
  const [confirming, setConfirming] = useState<{
    optionId: string;
    consequences: ChangeConsequences;
  } | null>(null);
  const confirmingId = confirming?.optionId ?? null;

  /** The request that stopped being waited on, so a stale deadline cannot silence a new one. */
  const [waitedOut, setWaitedOut] = useState<string | null>(null);
  const pendingSince = useRef<{ requestId: string; at: number } | null>(null);
  /** Counts the poll's ticks, so each tick arms the next whatever the read it asked for returned. */
  const [tick, setTick] = useState(0);

  /**
   * Only the newest answer is shown. A poll's read and a request's or a choice's answer can cross,
   * and the older landing last would put back options or a Goal the reader has just moved past.
   */
  const latest = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const useButtons = useRef(new Map<string, HTMLButtonElement>());
  /** The option just chosen here, whose "This is your goal." takes the focus once it is drawn. */
  const focusChosen = useRef<string | null>(null);
  const [returnFocusTo, setReturnFocusTo] = useState<string | null>(null);

  const issue = () => {
    latest.current += 1;

    return latest.current;
  };

  const load = useCallback(
    async (signal?: AbortSignal) => {
      latest.current += 1;
      const ticket = latest.current;

      try {
        const next = await fetchGoal(studyId, signal);
        if (alive.current && ticket === latest.current) {
          setView(next);
          setLoadError(null);
        }
      } catch (failure: unknown) {
        // A read given up on is not a read that failed: the deadline that aborted it says so itself.
        if (alive.current && ticket === latest.current && signal?.aborted !== true) {
          setLoadError(failureMessage(failure, 'Your goal could not be read.'));
        }
      }
    },
    [studyId],
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  /**
   * The focus waits on the first read to settle after a choice, and on no later one: by the time
   * this runs, a read that shows the choice has drawn its line and taken the focus. A read that
   * failed, or that shows another choice, gives up the move rather than leaving it for a later
   * read to make unprompted.
   */
  useEffect(() => {
    focusChosen.current = null;
  }, [view, loadError]);

  /**
   * Asks for options, shows what the server stored for the request, and then reads the whole step
   * again. The read is what makes the answer stick: a read started while the request was in flight —
   * the shell's own, after it records the step — can have looked before the request committed and
   * still land last, and the request is judged against the study's current inputs, which only a read
   * of the step brings up to date.
   */
  const ask = useCallback(
    (again: boolean) => {
      setAsking(true);
      setRequestError(null);
      const ticket = issue();

      void requestGoalOptions(studyId, { again }).then(
        (options) => {
          setAsking(false);
          if (alive.current && ticket === latest.current) {
            setView((current) => (current === null ? current : { ...current, options }));
          }
          void load();
        },
        (failure: unknown) => {
          setAsking(false);
          if (alive.current) {
            setRequestError(failureMessage(failure, 'Sigora could not be asked for goals.'));
          }
          void load();
        },
      );
    },
    [studyId, load],
  );

  const currentInputs = view?.currentInputs ?? null;
  const request = view?.options?.request ?? null;
  const set = view?.options?.set ?? null;
  /**
   * The options that can be chosen: a set drafted or reshaped for the study as it stands. A set for
   * older Materials, or another type or template, stays stored until a new one settles, but the
   * server refuses a choice from it, so it is not offered.
   */
  const usable: GoalOptionSet | null =
    set !== null && currentInputs !== null && sameGoalOptionsInputs(set.inputs, currentInputs)
      ? set
      : null;
  const forNow =
    request !== null &&
    currentInputs !== null &&
    sameGoalOptionsInputs(request.inputs, currentInputs)
      ? request
      : null;
  const pendingId = forNow?.status === 'pending' ? forNow.id : null;
  const waiting = pendingId !== null && waitedOut !== pendingId;

  /**
   * Arrival: ask once for the study's inputs when nothing on hand answers for them — no set drafted
   * for them, and no request for them still worth waiting on. Keyed on the inputs, so Materials
   * confirmed again while the step is open ask again, and a request that failed is not repeated on
   * every read; "Try again" is how it is asked for again.
   */
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (view === null || currentInputs === null || frozen) {
      return;
    }

    const key = canonicalJson(currentInputs);
    if (askedFor.current === key) {
      return;
    }

    askedFor.current = key;
    const stored = view.options === null ? null : { schema_version: 1 as const, ...view.options };
    if (goalOptionsRequestNeeded({ stored, inputs: currentInputs, now: new Date() })) {
      ask(false);
    }
  }, [view, currentInputs, frozen, ask]);

  /** The poll's read in flight, which the deadline abandons rather than waits on. */
  const pollRead = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      pollRead.current?.abort();
    },
    [],
  );

  const startWaiting = (requestId: string): number => {
    if (pendingSince.current?.requestId !== requestId) {
      pendingSince.current = { requestId, at: Date.now() };
    }

    return pendingSince.current.at;
  };

  /**
   * The deadline, on a timer of its own, armed once for each request waited on: a read that never
   * answers would otherwise hold the step pending for good, since the poll asks again only once its
   * read has answered.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    const since = startWaiting(pendingId);
    const deadline = setTimeout(
      () => {
        pollRead.current?.abort();
        setWaitedOut(pendingId);
      },
      Math.max(0, since + GOAL_OPTIONS_WAIT_MS - Date.now()),
    );

    return () => clearTimeout(deadline);
  }, [pendingId, waitedOut]);

  /**
   * One timer at a time, and none once the request has settled or the wait has run out. Each tick
   * arms the next only once its read has answered, so at most one read is in flight. Torn down with
   * the render that armed it, so nothing is left ticking when the step moves on.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    const since = startWaiting(pendingId);
    const overdue = () => Date.now() - since >= GOAL_OPTIONS_WAIT_MS;
    if (overdue()) {
      setWaitedOut(pendingId);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (overdue()) {
        setWaitedOut(pendingId);
        return;
      }

      const controller = new AbortController();
      pollRead.current = controller;
      void load(controller.signal).then(() => {
        if (alive.current && !controller.signal.aborted) {
          setTick((count) => count + 1);
        }
      });
    }, GOAL_OPTIONS_POLL_MS);

    return () => clearTimeout(timer);
  }, [pendingId, waitedOut, load, tick]);

  useEffect(() => {
    if (returnFocusTo !== null && confirmingId === null) {
      useButtons.current.get(returnFocusTo)?.focus();
      setReturnFocusTo(null);
    }
  }, [returnFocusTo, confirmingId]);

  const goal = view?.goal ?? null;
  const chosen = goal === null ? null : chosenGoalOption(goal.content);
  /**
   * The chosen Goal when it is not one of the options offered: it was chosen from a set drafted
   * for older inputs, or one reshaped since. It stays chosen, and is shown above the options.
   */
  const chosenElsewhere =
    goal !== null && chosen !== null && goal.content.option_set.id !== usable?.id ? chosen : null;
  /** Whether an option offered is the Goal already chosen: chosen, and from the set on offer. */
  const isChosen = (option: GoalOption) =>
    chosen !== null && chosenElsewhere === null && chosen.id === option.id;

  const choose = (option: GoalOption, acknowledgedDownstream: ChangeAcknowledgement | null) => {
    if (view === null || usable === null) {
      return;
    }

    setChoosing(option.id);
    setChooseError(null);
    setNotice(null);

    focusChosen.current = null;
    void selectGoal({
      studyId,
      optionSetId: usable.id,
      optionId: option.id,
      expectedVersion: view.goal?.version ?? null,
      acknowledgedDownstream,
    }).then(
      () => {
        setChoosing(null);
        setConfirming(null);
        // The button pressed goes once the read shows the choice, so focus moves to what replaces it.
        focusChosen.current = option.id;
        void load();
        onStudyChanged();
      },
      (failure: unknown) => {
        setChoosing(null);
        // A change that reaches anything below the Goal is made only once the reader has seen what
        // it does, as the server judged it.
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setConfirming({ optionId: option.id, consequences });
          return;
        }

        const code = domainCodeOf(failure);
        setConfirming(null);
        if (code === 'goal_options_changed' || code === 'artifact_version_not_current') {
          setNotice('Your goal options changed; look again.');
          void load();
          return;
        }

        if (code === 'goal_options_outdated') {
          setNotice(
            'Your study changed since these goals were drafted, so Sigora is drafting new ones.',
          );
          ask(false);
          // What changed under the options changed the study, so the steps and the sheet move too.
          onStudyChanged();
          return;
        }

        setChooseError(failureMessage(failure, 'That goal could not be chosen.'));
        if (code !== null) {
          void load();
        }
      },
    );
  };

  const cancelConfirm = () => {
    setReturnFocusTo(confirmingId);
    setConfirming(null);
  };

  if (view === null) {
    return loadError === null ? (
      <p data-test-id="goal-loading">Reading your goal…</p>
    ) : (
      <p className="chat-error" data-test-id="goal-error" role="alert">
        {loadError}{' '}
        <button type="button" data-test-id="goal-reload" onClick={() => void load()}>
          Try again
        </button>
      </p>
    );
  }

  const busy = asking || choosing !== null;
  const failed = forNow?.status === 'unavailable' || forNow?.status === 'unreadable';
  const overdue = pendingId !== null && !waiting;
  /**
   * A request refused or lost on the way stores nothing, so there is no failed request to show; the
   * refusal on screen is the only trace of it, and the retry is offered beside it.
   */
  const retryOffered =
    !frozen && currentInputs !== null && (failed || overdue || requestError !== null);

  return (
    <div className="goal" data-test-id="goal">
      <h2 className="goal-heading">What matters most to find out? Pick one.</h2>

      {notice !== null && (
        <p className="goal-notice" data-test-id="goal-notice" role="status">
          {notice}
        </p>
      )}

      {/*
        A read that failed over a step already on screen: what is shown is the last read that
        answered, so the step says it may be out of date rather than passing it off as current.
      */}
      {loadError !== null && (
        <p className="chat-error" data-test-id="goal-refresh-error" role="alert">
          Your goal could not be read again, so this may be out of date. {loadError}{' '}
          <button type="button" data-test-id="goal-refresh-retry" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}

      {currentInputs === null && usable === null && (
        <p className="goal-status" data-test-id="goal-needs-materials">
          Confirm your Materials first. Sigora drafts the goals from them.
        </p>
      )}

      <div aria-live="polite">
        {waiting && (
          <Pending live={false} className="goal-status" testId="goal-pending">
            Sigora is drafting three goals from your study type and Materials…
          </Pending>
        )}

        {overdue && (
          <p className="goal-status" data-test-id="goal-overdue">
            Sigora is taking longer than it should to draft your goals.
          </p>
        )}

        {forNow?.status === 'unavailable' && (
          <p className="goal-status" data-test-id="goal-unavailable">
            Sigora could not draft goals just now.
          </p>
        )}

        {forNow?.status === 'unreadable' && (
          <p className="goal-status" data-test-id="goal-unreadable">
            Sigora’s draft did not come back as three complete goals.
          </p>
        )}
      </div>

      {retryOffered && (
        <button type="button" data-test-id="goal-retry" disabled={busy} onClick={() => ask(true)}>
          Try again
        </button>
      )}

      {requestError !== null && (
        <p className="chat-error" data-test-id="goal-request-error" role="alert">
          {requestError}
        </p>
      )}

      {chooseError !== null && (
        <p className="chat-error" data-test-id="goal-choose-error" role="alert">
          {chooseError}
        </p>
      )}

      {goal !== null && chosenElsewhere !== null && (
        <section
          className="goal-option choice choice-selected"
          aria-labelledby="goal-chosen-label"
          data-option-id={chosenElsewhere.id}
          data-test-id="goal-chosen"
        >
          <p className="goal-label" id="goal-chosen-label">
            Your goal
          </p>
          {/*
            Chosen from options drafted for inputs the study no longer has: it stays chosen, and
            any options below are the ones the study's inputs now give.
          */}
          {currentInputs !== null &&
            !sameGoalOptionsInputs(goal.content.option_set.inputs, currentInputs) && (
              <p className="goal-notice" data-test-id="goal-inputs-changed" role="status">
                {GOAL_INPUTS_CHANGED_NOTICE}
              </p>
            )}
          <GoalHead option={chosenElsewhere} titleTestId="goal-chosen-title" />
          <GoalFacts option={chosenElsewhere} />
        </section>
      )}

      {usable !== null && (
        <ol className="goal-options" data-test-id="goal-options">
          {usable.options.map((option) => {
            const mine = isChosen(option);

            return (
              <li
                className={mine ? 'goal-option choice choice-selected' : 'goal-option choice'}
                data-option-id={option.id}
                data-test-id="goal-option"
                key={option.id}
              >
                <GoalHead option={option} titleTestId="goal-option-title" />

                <GoalFacts option={option} />

                {mine ? (
                  <p
                    className="goal-in-use"
                    data-test-id="goal-in-use"
                    tabIndex={-1}
                    ref={(line) => {
                      if (line !== null && focusChosen.current === option.id) {
                        focusChosen.current = null;
                        line.focus();
                      }
                    }}
                  >
                    This is your goal.
                  </p>
                ) : (
                  <button
                    type="button"
                    className="goal-use button-primary"
                    data-test-id="goal-use"
                    ref={(button) => {
                      if (button === null) {
                        useButtons.current.delete(option.id);
                      } else {
                        useButtons.current.set(option.id, button);
                      }
                    }}
                    disabled={frozen || busy || confirmingId !== null || currentInputs === null}
                    onClick={() => choose(option, null)}
                  >
                    {choosing === option.id ? 'Choosing…' : 'Use this goal'}
                  </button>
                )}

                {confirming !== null && confirming.optionId === option.id && (
                  <ChangeConfirm
                    consequences={confirming.consequences}
                    busy={busy}
                    onConfirm={() => choose(option, acknowledgementOf(confirming.consequences))}
                    onCancel={cancelConfirm}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}

      {goal !== null && (
        <VersionHistory
          testIdPrefix="goal"
          current={{
            version: goal.version,
            reasonText: view.history.find((entry) => entry.version === goal.version)?.reason ?? '',
          }}
          entries={view.history.map((entry) => ({
            version: entry.version,
            reasonText: historyLine(entry),
            at: entry.createdAt,
          }))}
        />
      )}
    </div>
  );
};
